import React from 'react';
import { useTranslation } from 'next-i18next';
import { Transition } from 'react-transition-group';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Collapsible } from '@components/animations';
import { useMediaQuery } from '@hooks/useMediaQuery';

import Card from '../Card/Card';
import Icon from '../Icon/Icon';
import Rating from '../Rating/Rating';

import styles from './RatingCard.module.scss';

const SIZES = {
  MEDIUM: 'medium',
  LARGE: 'large'
};

const ICON_SIZES = {
  [SIZES.MEDIUM]: 20,
  [SIZES.LARGE]: 28
};

const ELEVATION_LEVELS = [0, 1, 2, 3];

const RatingCard = (props) => {

  const {
    className,
    elevation,
    size = SIZES.MEDIUM,
    title,
    info,
    success,
    inline = false,
    disabled,
    rating,
    readOnly,
    submitted,
    withPadding,
    onChange = () => {},
    children
  } = props;

  const { t } = useTranslation();

  const isBreakpoint = useMediaQuery(`(max-width: ${styles.breakpoint})`);

  const classes = classnames(
    styles.root,
    { [styles[size]]: size },
    { [styles.inline]: inline },
    { [styles.disabled]: disabled },
    { [styles.simple]: !success },
    className
  );

  const ratingClasses = classnames(
    styles.rating,
    { [styles.submitted]: submitted }
  );

  // W/O success animation

  if (!success) {
    return (
      <Card
        className={classes}
        size={size}
        withPadding={withPadding}
        elevation={elevation}
      >
        <div className={styles.ratingContainer}>
          <Rating
            className={ratingClasses}
            symbolClassName={styles.symbol}
            size={isBreakpoint ? 40 : 32}
            symbol="star"
            value={rating}
            disabled={disabled}
            readOnly={readOnly}
            onChange={onChange}
          />
        </div>

        <div className={styles.textContainer}>
          <div className={styles.title}>{t(title)}</div>
          <div className={styles.info}>{t(info)}</div>
        </div>

        <div className={styles.content}>{children}</div>
      </Card>
    );
  }

  // With success animation

  return (
    <Card
      className={classes}
      size={size}
      withPadding={withPadding}
      elevation={elevation}
    >
      <div className={styles.ratingContainer}>

        {/* Rating */}
        <FadeAnimation
          isVisible={!submitted}
          persist
          yOffset={5}
          enterDelay={600}
        >
          <Rating
            className={ratingClasses}
            symbolClassName={styles.symbol}
            size={isBreakpoint ? 40 : 32}
            symbol="star"
            value={rating}
            readOnly={readOnly}
            onChange={onChange}
          />
        </FadeAnimation>

        {/* Check */}
        <FadeAnimation
          className={styles.check}
          isVisible={submitted}
          yOffset={-5}
          enterDelay={600}
        >
          <Icon name="check" size={ICON_SIZES[size]} />
        </FadeAnimation>

      </div>
      <div className={styles.textContainer}>

        {/* Title / Info */}
        <FadeAnimation
          isVisible={!submitted}
          persist
          yOffset={5}
          enterDelay={300}
        >
          <div className={styles.title}>{t(title)}</div>
          <div className={styles.info}>{t(info)}</div>
        </FadeAnimation>

        {/* Success */}
        <FadeAnimation
          className={styles.success}
          isVisible={submitted}
          yOffset={-5}
          enterDelay={300}
        >
          <div className={styles.title}>{t(success)}</div>
        </FadeAnimation>

        {/* Children */}
        {
          (children && !!children?.length && !!children.filter((c) => !!c).length) && (
            <Collapsible isExpanded={!submitted}>
              <div className={styles.inner}>
                {children}
              </div>
            </Collapsible>
          )
        }
      </div>
    </Card>
  );
};

// Components

const FadeAnimation = (props) => {
  const {
    className,
    isVisible,
    yOffset,
    persist,
    enterDuration = 200,
    enterDelay = 0,
    exitDuration = 200,
    exitDelay = 0,
    children
  } = props;

  const transitionRef = React.useRef(null);

  return (
    <Transition
      in={isVisible}
      nodeRef={transitionRef}
      mountOnEnter={!persist}
      unmountOnExit={!persist}
      timeout={{
        enter: enterDuration + enterDelay,
        exit: exitDuration + exitDelay
      }}
    >
      {
        (state) => {
          return (
            <div
              ref={transitionRef}
              className={classnames(styles.fade, { [styles[state]]: state }, className)}
              style={{
                '--enterDuration': `${enterDuration / 1000}s`,
                '--enterDelay': `${enterDelay / 1000}s`,
                '--exitDuration': `${exitDuration / 1000}s`,
                '--exitDelay': `${exitDelay / 1000}s`,
                '--yOffset': `${yOffset}px`
              }}
            >
              {children}
            </div>
          );
        }
      }
    </Transition>
  );
};

// Prop Types

RatingCard.propTypes = {
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  elevation: PropTypes.oneOf(ELEVATION_LEVELS),
  size: PropTypes.oneOf(Object.values(SIZES)),
  title: PropTypes.string,
  info: PropTypes.string,
  success: PropTypes.string,
  rating: PropTypes.number,
  inline: PropTypes.bool,
  readOnly: PropTypes.bool,
  submitted: PropTypes.bool,
  disabled: PropTypes.bool,
  withPadding: PropTypes.bool,
  onChange: PropTypes.func,
};

// Export

export default RatingCard;
